import React, { Suspense, lazy, memo, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import {
  setAllConfig,
  setAllConfigColors,
  setAllConfigRounded,
} from "../actions/config";
import { callInfoProfile } from "../actions/auth";
import { PublicRoute } from "./PublicRoute.jsx";
import { PrivateRoute } from "./PrivateRoute.jsx";
// import Routes from "./Routes.jsx";
import FooterPlataforma from "../components/company/FooterPlataforma.jsx";
import PreLoader from "../components/helpersUI/loading/PreLoader.jsx";
import NotFound from "../components/helpersUI/NotFound";
import can from "../casl/can";
import RoutesPublic from "./RoutesPublic.jsx";

const AuthRouter = lazy(() => import("./AuthRouter.jsx"));
const Routes = lazy(() => import("./Routes.jsx"));

const CompanyProvider = memo(({ companyName }) => {
  const dispatch = useDispatch();

  const { companyInstance, permissionsRoutes } = useSelector(
    (state) => state.company,
    (prev, next) => {
      // Devuelve true si solo quieres que el componente se actualice cuando cambie companyInstance
      return prev.companyInstance === next.companyInstance;
    }
  );

  const [statusRoutes, setStatusRoutes] = useState({
    providerLoaded: false,
    loadingData: true,
    errorData: false,
    isRedirected: false,
    isLoggedIn: false,
    msgError: "",
  });

  const loadedFinished = (isLogged) => {
    setStatusRoutes((prevState) => ({
      ...prevState,
      isLoggedIn: isLogged ?? true,
      providerLoaded: true,
      loadingData: false,
      errorData: false,
      msgError: "",
    }));
  };

  useEffect(() => {
    if (companyInstance && statusRoutes.loadingData) {
      const loadConfigData = () => {
        return new Promise((resolve, reject) => {
          companyInstance.db
            .collection("config")
            .doc("configApp")
            .collection("logos")
            .onSnapshot(
              (snapshot) => {
                const config = [];
                snapshot.forEach((doc) => {
                  config.push({
                    id: doc.id,
                    ...doc.data(),
                  });
                });
                dispatch(setAllConfig(config));
                resolve(); // Resuelve la Promesa cuando la consulta se completa con éxito
              },
              (error) => {
                // console.log("Error getting documents: ", error);
                reject(error); // Rechaza la Promesa en caso de error
              }
            );
        });
      };

      const loadConfigDataColor = () => {
        return new Promise((resolve, reject) => {
          companyInstance.db
            .collection("config")
            .doc("configApp")
            .collection("colores")
            .onSnapshot(
              (snapshot) => {
                const configColors = [];
                snapshot.forEach((doc) => {
                  configColors.push({
                    id: doc.id,
                    ...doc.data(),
                  });
                });
                dispatch(setAllConfigColors(configColors));
                resolve(); // Resuelve la Promesa cuando la consulta se completa con éxito
              },
              (error) => {
                // console.log("Error getting documents: ", error);
                reject(error); // Rechaza la Promesa en caso de error
              }
            );
        });
      };

      const loadConfigDataRounded = () => {
        return new Promise((resolve, reject) => {
          companyInstance.db
            .collection("config")
            .doc("configApp")
            .collection("rounded")
            .onSnapshot(
              (snapshot) => {
                const configRounded = [];
                snapshot.forEach((doc) => {
                  configRounded.push({
                    id: doc.id,
                    ...doc.data(),
                  });
                });
                dispatch(setAllConfigRounded(configRounded));
                resolve(); // Resuelve la Promesa cuando la consulta se completa con éxito
              },
              (error) => {
                // console.log("Error getting documents: ", error);
                reject(error); // Rechaza la Promesa en caso de error
              }
            );
        });
      };

      Promise.all([
        loadConfigData(),
        loadConfigDataColor(),
        loadConfigDataRounded(),
      ])
        .then(() => {
          // Ambas Promesas se completaron con éxito
          companyInstance.firebase.auth().onAuthStateChanged(async (user) => {
            if (user?.uid) {
              setStatusRoutes((prevState) => ({
                ...prevState,
                isLoggedIn: true,
              }));
              dispatch(callInfoProfile(user.uid, loadedFinished));
            } else {
              loadedFinished(false);
            }
          });
        })
        .catch((error) => {
          // Al menos una de las Promesas falló
          // console.error("Error loading data:", error);
          setStatusRoutes((prevState) => ({
            ...prevState,
            providerLoaded: true,
            loadingData: false,
            errorData: true,
            // msgError: error.toString(),
            msgError: `Error inesperado, contactar con el área TI de Saluta e indicar el código: ${error.toString()}`,
          }));
        });
    }
  }, [companyInstance]); // El efecto se ejecuta cuando loadingData cambia

  if (!statusRoutes.providerLoaded) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-r from-saluta-s to-saluta-c">
        <PreLoader
          text={"Cargando"}
          classText="text-white font-bold z-100"
          opacity={50}
          isLoading={!statusRoutes.providerLoaded}
        />
      </div>
    );
  }

  if (statusRoutes.errorData) {
    return (
      <div className="flex items-center justify-center min-h-screen text-center bg-gradient-to-r from-saluta-s to-saluta-c">
        <NotFound msg={statusRoutes.msgError} />
      </div>
    );
  }

  return (
    statusRoutes.providerLoaded &&
    !statusRoutes.loadingData && (
      <Suspense
        fallback={
          <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-r from-saluta-s to-saluta-c">
            <PreLoader
              text={"Cargando"}
              classText="text-white font-bold z-100"
              opacity={50}
              isLoading={true}
            />
          </div>
        }
      >
        <Router>
          <Switch>
            <PublicRoute
              path={`/${companyName}/auth`}
              company={companyName}
              isAuthenticated={statusRoutes.isLoggedIn}
              component={() => (
                <Suspense
                  fallback={
                    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-r from-saluta-s to-saluta-c">
                      <PreLoader
                        text={"Cargando"}
                        classText="text-white font-bold z-100"
                        opacity={50}
                        isLoading={true}
                      />
                    </div>
                  }
                >
                  <AuthRouter company={companyName} />
                </Suspense>
              )}
            />

            {statusRoutes.isLoggedIn && (
              <PrivateRoute
                exact
                path={`/${companyName}/`}
                company={companyName}
                isAuthenticated={statusRoutes.isLoggedIn}
                component={() => (
                  <Suspense
                    fallback={
                      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-r from-saluta-s to-saluta-c">
                        <PreLoader
                          text={"Cargando"}
                          classText="text-white font-bold z-100"
                          opacity={50}
                          isLoading={true}
                        />
                      </div>
                    }
                  >
                    <Routes
                      company={companyName}
                      isAuthenticated={statusRoutes.isLoggedIn}
                      can={can}
                    />
                  </Suspense>
                )}
              />
            )}

            {statusRoutes.isLoggedIn && (
              <Route
                exact
                path={`/${companyName}/consulta/:id`}
                component={lazy(() =>
                  import("../components/company/doctor/Consulta.jsx")
                )}
              />
            )}

            {statusRoutes.isLoggedIn && (
              <Route
                exact
                path={`/${companyName}/Ecuador/:id`}
                component={lazy(() =>
                  import("../components/company/doctor/chile/RecordEcuador.jsx")
                )}
              />
            )}
            {statusRoutes.isLoggedIn && (
              <Route
                exact
                path={`/${companyName}/Chile/:id`}
                component={lazy(() =>
                  import("../components/company/doctor/chile/RecordEcuador.jsx")
                )}
              />
            )}

            {statusRoutes.isLoggedIn && (
              <Route
                exact
                path={`/${companyName}/Colombia/:id`}
                component={lazy(() =>
                  import(
                    "../components/company/doctor/colombia/RecordColombia.jsx"
                  )
                )}
              />
            )}

            {statusRoutes.isLoggedIn && (
              <Route
                exact
                path={`/${companyName}/CostaRica/:id`}
                component={lazy(() =>
                  import(
                    "../components/company/doctor/costarica/RecordCostaRica.jsx"
                  )
                )}
              />
            )}

            <RoutesPublic
              companyName={companyName}
              permissionsRoutes={permissionsRoutes}
              statusRoutes={statusRoutes}
            />
            {/* <Redirect to={`/${companyName}/auth/login`} /> */}
          </Switch>
        </Router>

        {window.location.href.toString().includes("/cita/") ||
        window.location.href.toString().includes("/APK") ||
        window.location.href.toString().includes("/CEO") ||
        window.location.href.toString().includes("/CTO") ||
        window.location.href.toString().includes("/CFO") ? null : (
          <FooterPlataforma />
        )}
      </Suspense>
    )
  );
});

export default CompanyProvider;
